import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  Paper,
  Autocomplete,
  Chip,
  Checkbox
} from '@mui/material';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const HUNGARIAN_COUNTIES = [
  'Magyarország teljes területe',
  'Budapest',
  'Baranya',
  'Bács-Kiskun',
  'Békés',
  'Borsod-Abaúj-Zemplén',
  'Csongrád-Csanád',
  'Fejér',
  'Győr-Moson-Sopron',
  'Hajdú-Bihar',
  'Heves',
  'Jász-Nagykun-Szolnok',
  'Komárom-Esztergom',
  'Nógrád',
  'Pest',
  'Somogy',
  'Szabolcs-Szatmár-Bereg',
  'Tolna',
  'Vas',
  'Veszprém',
  'Zala',
];

const SocialRegisterComplete = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { completeSocialRegistration } = useAuth();

  const profile = location.state;

  useEffect(() => {
    if (!profile) {
      navigate('/register', { replace: true });
    }
  }, [profile, navigate]);

  const [userType, setUserType] = useState('CLIENT');
  const [businessName, setBusinessName] = useState('');
  const [city, setCity] = useState('');
  const [counties, setCounties] = useState([]);
  const [subscribedToNewsletter, setSubscribedToNewsletter] = useState(false);
  const [termsConsent, setTermsConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  if (!profile) return null;

  const validate = () => {
    const errs = {};
    if (userType === 'PROVIDER') {
      if (!businessName.trim()) errs.businessName = 'A vállalkozás neve kötelező';
      if (counties.length === 0) errs.counties = 'Legalább egy megye megadása kötelező';
    }
    if (!termsConsent) errs.termsConsent = 'A Felhasználási Feltételek elfogadása kötelező';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }
    setFieldErrors({});
    setSubmitting(true);

    const result = await completeSocialRegistration({
      googleId: profile.googleId || null,
      facebookId: profile.facebookId || null,
      email: profile.email || null,
      firstName: profile.firstName,
      lastName: profile.lastName,
      avatarUrl: profile.avatarUrl || null,
      userType,
      businessName: userType === 'PROVIDER' ? businessName.trim() : undefined,
      city: userType === 'PROVIDER' ? city.trim() : undefined,
      county: userType === 'PROVIDER' ? counties.join(',') : undefined,
      subscribedToNewsletter
    });

    setSubmitting(false);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error || 'Regisztráció sikertelen. Kérjük, próbálja újra.');
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" align="center" gutterBottom>
          Regisztráció befejezése
        </Typography>

        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
          Üdvözlünk, {profile.firstName}! Kérjük, add meg a hiányzó adatokat a regisztráció befejezéséhez.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Előtöltött, csak olvasható mezők */}
        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Social fiók adatai:
          </Typography>
          <Typography variant="body2">
            <strong>Név:</strong> {profile.firstName} {profile.lastName}
          </Typography>
          {profile.email && (
            <Typography variant="body2">
              <strong>Email:</strong> {profile.email}
            </Typography>
          )}
        </Box>

        <Box component="form" onSubmit={handleSubmit}>
          {/* Fiók típusa */}
          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <FormLabel component="legend">Regisztrálok mint:</FormLabel>
            <RadioGroup
              row
              value={userType}
              onChange={(e) => setUserType(e.target.value)}
              sx={{ mt: 1 }}
            >
              <FormControlLabel value="CLIENT" control={<Radio />} label="Ügyfél (esküvőt tervezek)" />
              <FormControlLabel value="PROVIDER" control={<Radio />} label="Szolgáltató (esküvői szolgáltatást kínálok)" />
            </RadioGroup>
          </FormControl>

          {/* PROVIDER mezők */}
          {userType === 'PROVIDER' && (
            <Box>
              <TextField
                fullWidth
                label="Vállalkozás neve *"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                error={!!fieldErrors.businessName}
                helperText={fieldErrors.businessName}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Város"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                sx={{ mb: 2 }}
              />

              <Autocomplete
                multiple
                options={HUNGARIAN_COUNTIES}
                value={counties}
                onChange={(_, newValue) => {
                  if (newValue.includes('Magyarország teljes területe')) {
                    setCounties(['Magyarország teljes területe']);
                  } else {
                    setCounties(newValue.filter(v => v !== 'Magyarország teljes területe'));
                  }
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} {...getTagProps({ index })} key={option} size="small" />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Szolgáltatási terület (megye) *"
                    error={!!fieldErrors.counties}
                    helperText={fieldErrors.counties || 'Válaszd ki, melyik megye(k)ben dolgozol'}
                  />
                )}
                sx={{ mb: 2 }}
              />
            </Box>
          )}

          {/* Hírlevél */}
          <FormControlLabel
            control={
              <Checkbox
                checked={subscribedToNewsletter}
                onChange={(e) => setSubscribedToNewsletter(e.target.checked)}
              />
            }
            label="Szeretnék hírlevelet kapni az újdonságokról"
            sx={{ mb: 1 }}
          />

          {/* Felhasználási feltételek */}
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={termsConsent}
                  onChange={(e) => setTermsConsent(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2">
                  Elolvastam és elfogadom {userType === 'PROVIDER' ? 'a' : 'az'}{' '}
                  <Link
                    to={userType === 'PROVIDER' ? '/partneri-aszf' : '/felhasznalasi-feltetelek'}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'inherit', fontWeight: 600 }}
                  >
                    {userType === 'PROVIDER' ? 'Partneri Általános Szerződési Feltételeket' : 'Általános Szerződési Feltételeket'}
                  </Link>
                  {' '}*
                </Typography>
              }
            />
            {fieldErrors.termsConsent && (
              <Typography variant="caption" color="error" sx={{ ml: 4, display: 'block' }}>
                {fieldErrors.termsConsent}
              </Typography>
            )}
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={submitting}
            sx={{ py: 1.5 }}
          >
            {submitting ? 'Regisztráció folyamatban...' : 'Regisztráció befejezése'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default SocialRegisterComplete;
